// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    onNotesChange?: (updatedNotes: string) => void;
    notes: string;
    showNotes: boolean;
    extendedStyles?: CartLineNotesStyles;
}

export interface CartLineNotesStyles {
    wrapper?: InjectableCss;
    textAreaWrapper?: InjectableCss;
    editableNotesTextArea?: TextAreaProps;
}

export const cartLineNotesStyles: CartLineNotesStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    textAreaWrapper: {
        css: css`
            width: 100%;
            margin-top: 5px;
        `,
    },
    editableNotesTextArea: {
        cssOverrides: {
            inputSelect: css`
                resize: none;
                padding: 8px;
                font-size: ${fontConfig.fontSize.xSmall}px;
                line-height: ${fontConfig.lineHeight.singleLine};
            `,
        },
    },
};

const LineNotes: FC<OwnProps> = ({ onNotesChange = () => {}, notes: lineNotes, showNotes, extendedStyles }) => {
    const [notes, setNotes] = useState(lineNotes);

    const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
    };

    const blurHandler = () => {
        onNotesChange(notes);
    };

    const [styles] = React.useState(() => mergeToNew(cartLineNotesStyles, extendedStyles));

    const lineLabel = showNotes ? "Hide Notes" : notes.length ? "Show Notes" : "Line Notes";

    return (
        <StyledWrapper {...styles.wrapper}>
            {showNotes && (
                <StyledWrapper {...styles.textAreaWrapper}>
                    <TextArea
                        {...styles.editableNotesTextArea}
                        value={notes}
                        onChange={changeHandler}
                        onBlur={blurHandler}
                        rows={2}
                        maxLength={300}
                        data-test-selector="cartline_notes"
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default LineNotes;
